import { StandardEvents } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

class DeskResetGallery extends HTMLElement {
  autoplayRevision = 0;

  connectedCallback() {
    this.controller?.abort();
    this.videoObserver?.disconnect();
    this.controller = new AbortController();
    this.selectedVideo = null;
    this.pendingAutoplay = null;
    this.autoplayVideo = null;
    this.autoplayRevision++;
    this.videoVisibility = new Map();
    const controls = this.querySelector('[data-gallery-controls]');
    if (controls) controls.hidden = this.querySelectorAll('button[data-gallery-src]').length < 2;
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button[data-gallery-src], button[data-gallery-direction]');
      if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
      if (button.dataset.galleryDirection !== undefined) {
        this.moveSelection(button.dataset.galleryDirection);
      } else {
        this.selectMedia(button);
      }
    }, { signal: this.controller.signal });

    const videos = [...this.querySelectorAll('video')];
    if (videos.length === 0) return;
    const { signal } = this.controller;
    for (const video of videos) {
      video.addEventListener('play', () => {
        if (this.pendingAutoplay?.querySelector('video') === video) this.pendingAutoplay = null;
      }, { signal });
      const onStopped = () => {
        if (video.paused && this.autoplayVideo === video) {
          this.autoplayVideo = null;
          this.autoplayRevision++;
        }
      };
      video.addEventListener('pause', onStopped, { signal });
      video.addEventListener('ended', onStopped, { signal });
    }
    if (window.IntersectionObserver) {
      this.videoObserver = new window.IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLVideoElement)) continue;
          this.videoVisibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
          if (!entry.isIntersecting || entry.intersectionRatio === 0) this.pauseVideo(entry.target);
        }
        this.maybeAutoplay();
      }, { threshold: [0, 0.5] });
      videos.forEach((video) => this.videoObserver.observe(video));
    } else {
      console.warn('Desk Reset gallery: video visibility tracking is unavailable; use the video controls.');
    }
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionPreference.addEventListener('change', this.onAutoplayPreferencesChange, { signal });
    navigator.connection?.addEventListener('change', this.onAutoplayPreferencesChange, { signal });
    document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
  }

  moveSelection(direction) {
    if (direction !== 'previous' && direction !== 'next') {
      console.error('Desk Reset gallery: the navigation direction is invalid.');
      return;
    }
    const thumbnails = [...this.querySelectorAll('button[data-gallery-src]')];
    if (thumbnails.length < 2) return;
    const current = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-pressed') === 'true');
    if (current < 0) {
      console.error('Desk Reset gallery: the current media selection is missing.');
      return;
    }
    const offset = direction === 'previous' ? -1 : 1;
    this.selectMedia(thumbnails[(current + offset + thumbnails.length) % thumbnails.length]);
  }

  selectMedia(button) {
    const image = this.querySelector('[data-gallery-image]');
    if (!(image instanceof HTMLImageElement) || !button.dataset.gallerySrc || !button.dataset.galleryAlt) {
      console.error('Desk Reset gallery: an image or its source/description is missing.');
      return;
    }
    const videoPanels = [...this.querySelectorAll('[data-gallery-video-id]')];
    const selectedVideo = button.dataset.galleryVideo
      ? videoPanels.find((panel) => panel.dataset.galleryVideoId === button.dataset.galleryVideo)
      : null;
    if (button.dataset.galleryVideo && !selectedVideo) {
      console.error('Desk Reset gallery: the selected video is missing.');
      return;
    }
    if (selectedVideo && !selectedVideo.querySelector('video')) {
      console.error('Desk Reset gallery: the selected video player is missing.');
      return;
    }
    if (selectedVideo !== this.selectedVideo) {
      this.autoplayRevision++;
      this.selectedVideo = selectedVideo;
      this.pendingAutoplay = selectedVideo?.hasAttribute('data-gallery-autoplay') && !this.autoplayDisabled()
        ? selectedVideo
        : null;
    }
    image.hidden = Boolean(selectedVideo);
    if (!selectedVideo) {
      image.src = button.dataset.gallerySrc;
      image.alt = button.dataset.galleryAlt;
    }
    videoPanels.forEach((panel) => {
      panel.hidden = panel !== selectedVideo;
      if (panel.hidden) panel.querySelectorAll('video').forEach((video) => this.pauseVideo(video));
    });
    const thumbnails = [...this.querySelectorAll('button[data-gallery-src]')];
    thumbnails.forEach((thumbnail) => {
      thumbnail.setAttribute('aria-pressed', String(thumbnail === button));
    });
    const status = this.querySelector('[data-gallery-status]');
    if (status) status.textContent = `${thumbnails.indexOf(button) + 1} / ${thumbnails.length}: ${button.dataset.galleryAlt}`;
    if (this.pendingAutoplay) {
      const video = this.pendingAutoplay.querySelector('video');
      this.videoVisibility.set(video, 0);
      this.videoObserver?.unobserve(video);
      this.videoObserver?.observe(video);
    }
  }

  autoplayDisabled() {
    return prefersReducedMotion() || navigator.connection?.saveData === true;
  }

  pauseVideo(video) {
    if (this.autoplayVideo === video) {
      this.autoplayVideo = null;
      this.autoplayRevision++;
    }
    video.pause();
  }

  maybeAutoplay() {
    const panel = this.pendingAutoplay;
    const video = panel?.querySelector('video');
    if (!video || panel !== this.selectedVideo || panel.hidden || !this.videoObserver ||
        this.controller.signal.aborted || document.hidden || this.autoplayDisabled() ||
        !(this.videoVisibility.get(video) >= 0.5)) return;
    this.pendingAutoplay = null;
    if (!video.paused) return;
    const revision = this.autoplayRevision;
    this.autoplayVideo = video;
    video.muted = true;
    video.playsInline = true;
    video.play().then(() => {
      if (this.controller.signal.aborted || panel !== this.selectedVideo || panel.hidden ||
          document.hidden || this.autoplayDisabled() || !(this.videoVisibility.get(video) > 0)) {
        this.pauseVideo(video);
      }
    }).catch((error) => {
      if (this.autoplayVideo === video && revision === this.autoplayRevision) this.autoplayVideo = null;
      if (error?.name === 'AbortError' && (video.paused || revision !== this.autoplayRevision)) return;
      if (error?.name === 'NotAllowedError') {
        console.warn('Desk Reset gallery: autoplay was prevented; use the video controls.', error);
      } else {
        console.error('Desk Reset gallery: automatic video playback failed.', error);
      }
    });
  }

  onAutoplayPreferencesChange = () => {
    if (!this.autoplayDisabled()) return;
    this.pendingAutoplay = null;
    if (this.autoplayVideo) this.pauseVideo(this.autoplayVideo);
  };

  onVisibilityChange = () => {
    if (document.hidden) {
      this.querySelectorAll('video').forEach((video) => this.pauseVideo(video));
    } else {
      this.maybeAutoplay();
    }
  };

  disconnectedCallback() {
    this.controller?.abort();
    this.videoObserver?.disconnect();
    this.pendingAutoplay = null;
    this.selectedVideo = null;
    this.querySelectorAll('video').forEach((video) => this.pauseVideo(video));
  }
}

class DeskResetHeader extends HTMLElement {
  cartCountRevision = 0;

  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('a')) return;
      this.querySelectorAll('details').forEach((menu) => {
        if (menu instanceof HTMLDetailsElement) menu.open = false;
      });
    }, { signal: this.controller.signal });

    if (this.querySelector('[data-dr-cart-count]')) {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, {
        signal: this.controller.signal,
      });
      window.addEventListener('pageshow', this.onPageShow, { signal: this.controller.signal });
      this.refreshCartCount(false).catch(this.handleCartCountError);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  onCartUpdate = (event) => {
    if (!event.promise) {
      this.refreshCartCount(true).catch(this.handleCartCountError);
      return;
    }

    event.promise
      .then(({ cart }) => {
        const itemCount = cart?.totalQuantity;

        if (!Number.isSafeInteger(itemCount) || itemCount < 0) {
          return this.refreshCartCount(true);
        }

        this.renderCartCount(itemCount, true);
      })
      .catch(this.handleCartCountError);
  };

  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshCartCount(false).catch(this.handleCartCountError);
    }
  };

  handleCartCountError = (error) => {
    if (error?.name !== 'AbortError') {
      console.error('[desk-reset-header] Unable to update the cart count:', error);
    }
  };

  async refreshCartCount(animate) {
    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) throw new Error('The cart URL is missing from the Desk Reset header.');
    const revision = ++this.cartCountRevision;
    const { signal } = this.controller;

    const response = await fetch(`${cartUrl}.js`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);

    const cart = await response.json();
    const itemCount = Number(cart.item_count);
    if (!Number.isSafeInteger(itemCount) || itemCount < 0) {
      throw new Error('The cart response did not include a valid item count.');
    }

    if (!signal.aborted && revision === this.cartCountRevision) {
      this.renderCartCount(itemCount, animate);
    }
  }

  renderCartCount(itemCount, animate) {
    this.cartCountRevision++;
    const previousCount = Number(this.dataset.cartCount);
    const displayCount = itemCount > 99 ? '99+' : String(itemCount);
    const cartLabel = this.dataset.cartLabel || 'Cart';
    const cartCountLabel = this.dataset.cartCountLabel || 'Total items in cart';
    const countChanged = itemCount !== previousCount;

    this.dataset.cartCount = String(itemCount);

    this.querySelectorAll('[data-dr-cart-link]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      link.setAttribute('aria-label', itemCount > 0 ? `${cartLabel}, ${cartCountLabel}: ${itemCount}` : cartLabel);
    });

    this.querySelectorAll('[data-dr-cart-count]').forEach((badge) => {
      if (!(badge instanceof HTMLElement)) return;
      badge.hidden = itemCount === 0;
      badge.textContent = displayCount;
      badge.classList.remove('dr-cart-count--updated');

      if (animate && countChanged && itemCount > 0) {
        void badge.offsetWidth;
        badge.classList.add('dr-cart-count--updated');
      }
    });

    if (animate && countChanged) {
      const status = this.querySelector('[data-dr-cart-status]');
      if (status) status.textContent = `${cartCountLabel}: ${itemCount}`;
    }
  }
}

if (!customElements.get('desk-reset-gallery')) customElements.define('desk-reset-gallery', DeskResetGallery);
if (!customElements.get('desk-reset-header')) customElements.define('desk-reset-header', DeskResetHeader);
